import { Stack } from "@mui/material";
import { useEffect } from "react";
import { DatePicker } from "../../../features/date-picker/ui/DatePicker";
import type { Dayjs } from "dayjs";
import { Deployment } from "../../../types/Deployment";
import { useDeploymentNavController } from "../model/use-deployment-nav-controller";
import { DateNavigationLayout } from "../../../features/date-navigation";

export interface DeploymentNavLayoutProps {
    timezone?: string;
    siteId?: string;
    hideNavButtons?: boolean;
    onDateChange: (date: Date | null) => void;
    onDeploymentChange: (deployment: Deployment | null) => void;
}

export const DeploymentNavLayout: React.FC<DeploymentNavLayoutProps> = ({
    timezone = "UTC",
    siteId,
    hideNavButtons = true,
    onDateChange,
    onDeploymentChange,
}) => {
    const {
        selectedDate,
        setSelectedDate,
        selectedDeployment,
        dates,
        shouldDisableDate,
    } = useDeploymentNavController(siteId);

    // Keep previous values to avoid calling callbacks unnecessarily
    // if the values have not changed.
    // TODO: Consider using useRef or useMemo to optimize further.
    // This is a dirty fix to get ready before OPG demo.
    const prevRef = (globalThis as any).__navLayoutPrev || {};

    useEffect(() => {
        if (onDateChange && prevRef.selectedDate !== selectedDate)
            onDateChange(selectedDate ? new Date(selectedDate) : null);
    }, [selectedDate, onDateChange]);

    useEffect(() => {
        if (onDeploymentChange)
            onDeploymentChange(selectedDeployment ? selectedDeployment : null);
    }, [selectedDeployment, onDeploymentChange]);

    const disableNavigation = !(selectedDate && dates.length !== 0);

    return (
        <Stack direction="row" spacing={2} alignItems="center">
            <DatePicker
                value={selectedDate ? new Date(selectedDate) : null}
                onChange={setSelectedDate}
                minDate={dates.length ? dates[0] : null}
                maxDate={dates.length ? dates[dates.length - 1] : null}
                shouldDisableDate={
                    shouldDisableDate
                        ? (d: Dayjs) => shouldDisableDate(d ? d.toDate() : null)
                        : undefined
                }
                timezone={timezone}
                disabled={disableNavigation}
            />
            {!hideNavButtons && (
                <DateNavigationLayout
                    selectedDate={selectedDate ? new Date(selectedDate) : null}
                    dates={dates}
                    timezone={timezone}
                    onDateChange={setSelectedDate}
                    disabled={disableNavigation}
                />
            )}
        </Stack>
    );
};
